import { useEffect, useRef } from 'react';
import type { WsMessage } from '@vst/shared';
import { apiBase } from '../lib/http';
import { getInitData } from '../lib/telegram';

/**
 * Подключение к /ws Durable Object — real-time обновления магазина.
 * Авто-reconnect с backoff, heartbeat пингом.
 */
export function useWebSocket(onMessage: (msg: WsMessage) => void): void {
  const onRef = useRef(onMessage);
  onRef.current = onMessage;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let closed = false;
    let retry = 0;
    let pingInterval: ReturnType<typeof setInterval> | null = null;

    const connect = () => {
      if (closed) return;
      const base = apiBase().replace(/^http/, 'ws');
      const initData = encodeURIComponent(getInitData());
      if (!initData) return;

      ws = new WebSocket(`${base}/ws?initData=${initData}`);

      ws.onopen = () => {
        retry = 0;
        pingInterval = setInterval(() => {
          try { ws?.send(JSON.stringify({ type: 'ping' })); } catch { /* noop */ }
        }, 30_000);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data) as WsMessage;
          onRef.current(msg);
        } catch {
          /* ignore */
        }
      };

      ws.onclose = () => {
        if (pingInterval) clearInterval(pingInterval);
        if (closed) return;
        retry = Math.min(retry + 1, 6);
        setTimeout(connect, Math.min(1000 * 2 ** retry, 30_000));
      };

      ws.onerror = () => { try { ws?.close(); } catch { /* noop */ } };
    };

    connect();
    return () => {
      closed = true;
      if (pingInterval) clearInterval(pingInterval);
      try { ws?.close(); } catch { /* noop */ }
    };
  }, []);
}
